import { all, call, delay, fork, put, race, take, takeLatest } from 'redux-saga/effects';

import { restClient } from 'config/store/configureStore';
import { ENTITY_TYPE } from 'modules/app/constants';
import { simpleRequestsLoader } from 'modules/app/utils';
import type { ApiError, PayloadAction } from 'modules/global/generic.types';
import * as actions from 'modules/kiosks/actions';
import * as types from 'modules/kiosks/actionTypes';
import requestsConfig from 'modules/kiosks/requestsConfig';
import dashboardSaga from 'modules/kiosks/sagas/dashboard';
import detailsSaga from 'modules/kiosks/sagas/details';
import type { KioskStatusSocketPayload, RequestKioskStatusAction } from 'modules/kiosks/types';
import { actionTypeFormatter } from 'modules/sockets/actionTypes';
import { SOCKET_ACTION } from 'modules/sockets/constants';
import { createNotification } from 'modules/toast-notifications/actions';

const WS_REQUEST_KIOSK_STATUS_UPDATE = actionTypeFormatter(
  undefined,
  ENTITY_TYPE.KIOSK_STATUS,
  SOCKET_ACTION.KIOSK_STATUS_DETAILS
);

const STATUS_REQUEST_TIMEOUT = 10000;

function* requestKioskStatus({ payload, meta }: RequestKioskStatusAction) {
  try {
    yield call(() => restClient.request<unknown, unknown, undefined, Record<string, unknown>>(payload.request));
    const { timeout, response }: {
      timeout?: true;
      response?: PayloadAction<KioskStatusSocketPayload>;
    } = yield race({
      timeout: delay(STATUS_REQUEST_TIMEOUT),
      response: take(WS_REQUEST_KIOSK_STATUS_UPDATE),
    });
    if (timeout || !response) {
      throw new Error('Kiosk status request timed out');
    }
    yield put(actions.requestKioskStatusSuccess(response.payload, meta));
  } catch (error) {
    yield put(actions.requestKioskStatusFailure(error as ApiError | Error, meta));
    yield put(createNotification({
      type: 'error',
      message: {
        id: 'http.error.message',
        defaultMessage: 'Oops! Something went wrong.',
      },
    }));
  }
}

export default function* KioskManagementSagas() {
  yield all(simpleRequestsLoader(requestsConfig));
  yield takeLatest(types.REQUEST_KIOSK_STATUS, requestKioskStatus);

  yield fork(dashboardSaga);
  yield fork(detailsSaga);
}
